import { Language } from '@yleisradio/areena-types';
import { BrowseMenuContent } from '@yleisradio/areena-types/domain/v2/browseMenuContent';
import '@yleisradio/tunnus-sdk/lib/tunnus-sdk.css';
import { AreenaFooter } from 'components/AreenaFooter';
import BrowserSupportAlert from 'components/BrowserSupportAlert';
import { Container } from 'components/Container';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { LastenAreenaInfo } from 'components/LastenAreenaInfo';
import { Navigation } from 'components/Navigation';
import { NotificationToaster } from 'components/Notifications';
import { Notification } from 'components/Notifications/Notification';
import { SkipLink } from 'components/SkipLink';
import { YleAnalytics } from 'components/YleAnalytics';
import { AnalyticsAvailabilityProvider } from 'contexts/AnalyticsAvailabilityContext';
import { AreenaServiceProvider } from 'contexts/AreenaServiceContext';
import { BrazeSdkProvider } from 'contexts/BrazeSdkContext';
import { CookieConsentContextProvider } from 'contexts/CookieConsentContext';
import { EscHandlerProvider } from 'contexts/EscHandlerContext/EscHandlerContext';
import { LocationContextProvider } from 'contexts/LocationContext';
import { PlayerStateProvider } from 'contexts/PlayerStateContext';
import { ProgressCacheInvalidatorProvider } from 'contexts/ProgressCacheInvalidatorContext';
import { SnowplowProvider } from 'contexts/SnowplowContext';
import { TunnusContextProvider } from 'contexts/TunnusContext';
import { useTranslation } from 'hooks/useTranslation';
import type {
  AppContext,
  AppInitialProps,
  AppProps as NextAppProps,
} from 'next/app';
import NextApp from 'next/app';
import { NextApiRequestCookies } from 'next/dist/server/api-utils';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import React, { useEffect } from 'react';
import * as ApmRum from 'services/apm-rum';
import { fetchBrowseMenuContentData } from 'services/areena-api/fetchers';
import { chatScriptUrl } from 'services/properties/frontend';
import 'styles/global.scss';
import { SWRConfig } from 'swr';
import { globalSWRConfig } from 'utils/fetch';
import { hasTunnusSessionCookie } from 'utils/login';

ApmRum.init();

const cleanUpUnusedLocalStorageItem = (): void => {
  window.localStorage.removeItem('isPodcastsTooltipDismissed');
  window.localStorage.removeItem('guide/regional-channel/yle-radio-suomi');
  window.localStorage.removeItem('guide/regional-channel/yle-radio-vega');
};

type AppProps = {
  hasTunnusSessionCookie: boolean;
  browseMenuContent: BrowseMenuContent | null;
  requestFromLastenAreena: boolean;
};

type AppRequest = {
  cookies?: NextApiRequestCookies;
};

const Player = dynamic(() => import('../components/Player'));

function App({
  pageProps,
  Component,
  hasTunnusSessionCookie,
  browseMenuContent,
  requestFromLastenAreena,
}: AppProps & NextAppProps): React.ReactElement {
  useEffect(cleanUpUnusedLocalStorageItem, []);

  const t = useTranslation();

  const errorFallback = (
    <Container>
      <Notification
        helpText={t('notificationHelpTextGeneric')}
        notification={{ uiMessage: t('genericError') }}
        notificationStyle="error"
      />
    </Container>
  );

  return (
    <ErrorBoundary fallback={errorFallback}>
      <div key={'browserSupport'}>
        <BrowserSupportAlert />
      </div>
      <SWRConfig value={globalSWRConfig}>
        <AnalyticsAvailabilityProvider>
          <TunnusContextProvider
            hasTunnusSessionCookie={hasTunnusSessionCookie}
          >
            <CookieConsentContextProvider>
              <BrazeSdkProvider>
                <SnowplowProvider>
                  <AreenaServiceProvider>
                    <SkipLink />
                    <YleAnalytics />
                    <EscHandlerProvider>
                      {requestFromLastenAreena && <LastenAreenaInfo />}
                      <Navigation browseMenuContent={browseMenuContent} />
                      <ProgressCacheInvalidatorProvider>
                        <LocationContextProvider>
                          <PlayerStateProvider>
                            <ErrorBoundary fallback={errorFallback}>
                              <Component {...pageProps} />
                              <AreenaFooter />
                              <Player />
                            </ErrorBoundary>
                          </PlayerStateProvider>
                        </LocationContextProvider>
                      </ProgressCacheInvalidatorProvider>
                      <NotificationToaster />
                    </EscHandlerProvider>
                  </AreenaServiceProvider>
                </SnowplowProvider>
              </BrazeSdkProvider>
            </CookieConsentContextProvider>
          </TunnusContextProvider>
        </AnalyticsAvailabilityProvider>
      </SWRConfig>
      <Script src={chatScriptUrl} strategy="lazyOnload" />
    </ErrorBoundary>
  );
}

App.getInitialProps = async (
  appContext: AppContext
): Promise<AppInitialProps & AppProps> => {
  const cookies = (appContext.ctx.req as AppRequest)?.cookies;
  const { locale, query } = appContext.router;
  const language: Language = locale === 'sv' ? 'sv' : 'fi';

  const appProps = await NextApp.getInitialProps(appContext);

  const browseMenuContent = await fetchBrowseMenuContentData(language);

  return {
    ...appProps,
    hasTunnusSessionCookie: hasTunnusSessionCookie(cookies),
    browseMenuContent,
    requestFromLastenAreena: isRequestFromLastenAreena(query),
  };
};

export default App;

function isRequestFromLastenAreena(query: { source?: string }): boolean {
  if ('source' in query) {
    const source = query['source'];
    return source === 'lasten-areena' || source === 'barnens-arena';
  }
  return false;
}
